import { useEffect, useState } from "react";
import { Brand } from "@/components/Logo";
import { toast } from "sonner";
import { Loader2, Send, FileText, Link2, Copy, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  nuova_valutazione: "Nuova valutazione",
  awaiting_payment: "In attesa pagamento",
  paid: "Visure pagate",
  in_lavorazione: "In lavorazione",
  visure_pronte: "Visure pronte",
  istruttoria: "Pratica attiva (a risultato)",
  attesa_banca: "In attesa risposta banca (max 90 gg)",
  cancellata: "CANCELLATA",
  non_cancellata: "Non cancellata",
  saldata: "Saldata",
};

const STATUS_COLORS = {
  nuova_valutazione: "text-[#00AEEF] border-[#00AEEF]/40 bg-[#E6F6FD]",
  awaiting_payment: "text-[#64748B] border-[#E2E8F0] bg-white",
  paid: "text-[#003087] border-[#003087]/30 bg-[#E6F6FD]",
  in_lavorazione: "text-sky-700 border-sky-300 bg-sky-50",
  visure_pronte: "text-sky-700 border-sky-300 bg-sky-50",
  istruttoria: "text-violet-700 border-violet-300 bg-violet-50",
  attesa_banca: "text-amber-700 border-amber-300 bg-amber-50",
  cancellata: "text-[#0077A3] border-[#00AEEF]/50 bg-[#E6F6FD]",
  non_cancellata: "text-red-600 border-red-300 bg-red-50",
  saldata: "text-[#0077A3] border-[#00AEEF]/60 bg-[#E6F6FD]",
};

const STEP_LABELS = { visure: "VISURE · 99€", cancellazione: "CANCELLAZIONE · da 999€ a risultato", valutazione: "VALUTAZIONE GRATUITA" };

const PKG_LABELS = { cancellazione_999: "999€ (1 cancellazione)", cancellazione_1799: "1.799€ (2-3 cancellazioni)", cancellazione_2499: "2.499€ (4+ cancellazioni)" };

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("cr_admin") || null);
  const [login, setLogin] = useState({ email: "", password: "" });
  const [orders, setOrders] = useState([]);
  const [pec, setPec] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [letters, setLetters] = useState({});
  const [selected, setSelected] = useState({});
  const [saldoPkg, setSaldoPkg] = useState({});
  const [esito, setEsito] = useState({});
  const [popupStats, setPopupStats] = useState(null);
  const [guideLeads, setGuideLeads] = useState([]);
  const [statsVal, setStatsVal] = useState("");
  const [loading, setLoading] = useState(false);

  const auth = { Authorization: `Bearer ${token}` };

  const load = async (tok = token) => {
    const [o, p, s, g] = await Promise.all([
      fetch(`${API}/admin/orders`, { headers: { Authorization: `Bearer ${tok}` } }),
      fetch(`${API}/admin/pec`, { headers: { Authorization: `Bearer ${tok}` } }),
      fetch(`${API}/admin/popup-stats`, { headers: { Authorization: `Bearer ${tok}` } }),
      fetch(`${API}/admin/guide-leads`, { headers: { Authorization: `Bearer ${tok}` } }),
    ]);
    if (o.status === 401) return logout();
    setOrders(await o.json());
    setPec(await p.json());
    if (s.ok) setPopupStats(await s.json());
    if (g.ok) setGuideLeads((await g.json()).leads || []);
    try {
      const st = await fetch(`${API}/stats`);
      if (st.ok) {
        const sd = await st.json();
        setStatsVal(sd.segnalazioni_cancellate != null ? String(sd.segnalazioni_cancellate) : "");
      }
    } catch {}
  };

  const saveStats = async () => {
    const r = await fetch(`${API}/admin/stats`, {
      method: "POST",
      headers: { ...auth, "Content-Type": "application/json" },
      body: JSON.stringify({ segnalazioni_cancellate: parseInt(statsVal || "0", 10) || 0 }),
    });
    if (r.ok) toast.success("Contatore aggiornato in homepage");
    else toast.error("Errore salvataggio contatore");
  };

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(login),
      });
      if (!r.ok) throw new Error();
      const d = await r.json();
      localStorage.setItem("cr_admin", d.token);
      setToken(d.token);
    } catch {
      toast.error("Credenziali non valide");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("cr_admin");
    setToken(null);
  };

  const setStatus = async (id, status) => {
    await fetch(`${API}/admin/orders/${id}/status`, {
      method: "POST", headers: { ...auth, "Content-Type": "application/json" }, body: JSON.stringify({ status }),
    });
    toast.success(`Stato → ${STATUS_LABELS[status]}`);
    load();
  };

  const loadLetters = async (id) => {
    const r = await fetch(`${API}/admin/orders/${id}/letters`, { headers: auth });
    const data = await r.json();
    setLetters((prev) => ({ ...prev, [id]: data }));
  };

  const genLetters = async (id) => {
    const ids = selected[id] || [];
    if (!ids.length) return toast.error("Seleziona almeno un istituto");
    const r = await fetch(`${API}/admin/orders/${id}/letters`, {
      method: "POST", headers: { ...auth, "Content-Type": "application/json" }, body: JSON.stringify({ institution_ids: ids }),
    });
    const d = await r.json();
    toast.success(`${d.created} lettere generate`);
    loadLetters(id);
  };

  const sendLetter = async (orderId, letterId) => {
    const r = await fetch(`${API}/admin/letters/${letterId}/send`, { method: "POST", headers: auth });
    const d = await r.json();
    toast.success(d.status === "inviata_simulata" ? "Invio simulato (configura PEC per invio reale)" : "Lettera inviata via PEC");
    loadLetters(orderId);
  };

  const saldoLink = async (id) => {
    const pkg = saldoPkg[id] || "cancellazione_999";
    const r = await fetch(`${API}/admin/orders/${id}/saldo-link?lookup_key=${pkg}`, { method: "POST", headers: auth });
    const d = await r.json();
    if (!r.ok) return toast.error(d.detail || "Errore");
    navigator.clipboard?.writeText(d.saldo_url);
    toast.success(`Link saldo ${PKG_LABELS[pkg]} generato e copiato`);
    load();
  };

  const saveEsito = async (id) => {
    const e = esito[id] || {};
    const r = await fetch(`${API}/admin/orders/${id}/esito-visure`, {
      method: "POST", headers: { ...auth, "Content-Type": "application/json" },
      body: JSON.stringify({ segnalazioni: Number(e.count ?? 0), note: e.note || "" }),
    });
    const d = await r.json();
    if (!r.ok) return toast.error(d.detail || "Errore");
    toast.success(`Esito salvato → pacchetto proposto: ${d.label} (${d.price}€)`);
    load();
  };

  const inviaOfferta = async (id) => {
    const r = await fetch(`${API}/admin/orders/${id}/offerta`, { method: "POST", headers: auth });
    const d = await r.json();
    if (!r.ok) return toast.error(d.detail || "Errore");
    navigator.clipboard?.writeText(d.url);
    toast.success(d.email_id ? "Email con offerta inviata al cliente (link copiato)" : "Link offerta copiato (email non inviata)");
    load();
  };

  const inviaSblocco = async (id) => {
    const r = await fetch(`${API}/admin/orders/${id}/sblocco-link`, { method: "POST", headers: auth });
    const d = await r.json();
    if (!r.ok) return toast.error(d.detail || "Errore");
    navigator.clipboard?.writeText(d.url);
    toast.success(d.email_id ? "Email «paga e sblocca» inviata al cliente (link copiato)" : "Link sblocco copiato (email non inviata)");
    load();
  };

  const uploadVisura = async (id, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const r = await fetch(`${API}/admin/orders/${id}/visura-pulita`, { method: "POST", headers: auth, body: fd });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) return toast.error(d.detail || "Errore upload");
    toast.success("Visura pulita caricata");
    load();
  };

  const apriFirma = async (id, fase) => {
    const r = await fetch(`${API}/admin/orders/${id}/firma-img/${fase}`, { headers: auth });
    if (!r.ok) return toast.error("Firma non presente");
    const blob = await r.blob();
    window.open(URL.createObjectURL(blob), "_blank");
  };

  const apriAllegato = async (id, kind) => {
    const r = await fetch(`${API}/admin/orders/${id}/allegato/${kind}`, { headers: auth });
    if (!r.ok) return toast.error("Allegato non presente");
    const blob = await r.blob();
    window.open(URL.createObjectURL(blob), "_blank");
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-5 hero-glow" data-testid="admin-login-page">
        <form onSubmit={doLogin} className="card w-full max-w-sm rounded-3xl p-8">
          <Brand />
          <h1 className="text-xl font-bold text-[#003087] mt-8">Area Riservata</h1>
          <input data-testid="admin-email" type="email" required placeholder="Email" value={login.email}
            onChange={(e) => setLogin({ ...login, email: e.target.value })} className="input-field mt-6" />
          <input data-testid="admin-password" type="password" required placeholder="Password" value={login.password}
            onChange={(e) => setLogin({ ...login, password: e.target.value })} className="input-field mt-3" />
          <button data-testid="admin-login-btn" disabled={loading} className="btn-green w-full mt-6 rounded-full py-3 text-sm font-bold flex items-center justify-center gap-2">
            {loading && <Loader2 size={15} className="animate-spin" />} Accedi
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-5 lg:px-8 py-10 max-w-6xl mx-auto" data-testid="admin-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <Brand />
        <div className="flex gap-3">
          <button data-testid="admin-refresh" onClick={() => load()} className="btn-outline rounded-full px-4 py-2 text-xs font-bold">Aggiorna</button>
          <button data-testid="admin-logout" onClick={logout} className="btn-outline rounded-full px-4 py-2 text-xs font-bold">Esci</button>
        </div>
      </div>

      <h1 className="text-2xl font-extrabold text-[#003087] mt-10">Richieste clienti <span className="text-[#00AEEF]">({orders.length})</span></h1>

      <div className="card rounded-2xl p-5 mt-6" data-testid="stats-editor">
        <h2 className="text-sm font-extrabold text-[#003087]">Contatore risultati in homepage</h2>
        <p className="text-[11px] text-[#94A3B8] mt-1">Il numero appare in homepage con animazione. Se lo lasci a 0 la sezione resta nascosta.</p>
        <div className="mt-3 flex items-center gap-3 max-w-sm">
          <input data-testid="stats-input" type="number" min="0" value={statsVal}
            onChange={(e) => setStatsVal(e.target.value)}
            className="input-field text-lg font-extrabold text-[#003087] w-36" placeholder="0" />
          <button data-testid="stats-save" onClick={saveStats}
            className="btn-green rounded-full px-5 py-3 text-xs font-bold">Salva</button>
        </div>
      </div>

      {guideLeads.length > 0 && (
        <div className="card rounded-2xl p-5 mt-6" data-testid="guide-leads-card">
          <h2 className="text-sm font-extrabold text-[#003087]">Contatti dalla guida gratuita <span className="text-[#00AEEF]">({guideLeads.length})</span></h2>
          <p className="text-[11px] text-[#94A3B8] mt-1">Hanno scaricato il PDF delle 4 visure: richiamali a caldo.</p>
          <div className="mt-3 space-y-2">
            {guideLeads.map((l, i) => (
              <div key={i} data-testid={`guide-lead-${i}`} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm border-b border-[#F1F5F9] pb-2">
                <span className="font-bold text-[#003087]">{l.name}</span>
                <a href={`tel:${l.phone}`} className="text-[#00AEEF] font-semibold">{l.phone}</a>
                <span className="text-[11px] text-[#94A3B8]">{new Date(l.created_at).toLocaleString("it-IT")}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {popupStats && (
        <div className="card rounded-2xl p-5 mt-6" data-testid="popup-stats">          <h2 className="text-sm font-extrabold text-[#003087]">A/B Test popup di uscita</h2>
          <p className="text-[11px] text-[#94A3B8] mt-1">A = «Aspetta! Prima di rinunciare al tuo mutuo…» · B = «Quante segnalazioni hai? Scoprilo prima di decidere.»</p>
          <div className="mt-4 grid sm:grid-cols-2 gap-3 max-w-xl">
            {["a", "b"].map((v) => {
              const s = popupStats[v];
              const clicks = s.whatsapp + s.order;
              const ctr = s.shown ? Math.round((clicks / s.shown) * 100) : 0;
              return (
                <div key={v} className="bg-[#F6FAFD] border border-[#E2E8F0] rounded-xl p-4" data-testid={`popup-stats-${v}`}>
                  <p className="text-xs font-bold text-[#00AEEF] uppercase tracking-wider">Variante {v.toUpperCase()}</p>
                  <p className="text-3xl font-extrabold text-[#003087] mt-1.5">{s.shown} <span className="text-xs font-semibold text-[#64748B]">visualizzazioni</span></p>
                  <p className="text-[11px] text-[#64748B] mt-1.5">Click WhatsApp: <strong className="text-[#1A1A1A]">{s.whatsapp}</strong> · Click ordine visure: <strong className="text-[#1A1A1A]">{s.order}</strong></p>
                  <p className="text-[11px] font-bold text-[#0077A3] mt-1.5">CTR {ctr}%</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-6 space-y-4">
        {orders.map((o) => (
          <div key={o.id} className="card rounded-2xl p-5" data-testid={`admin-order-${o.id.slice(0, 8)}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-bold text-[#003087]">{o.name} <span className="text-[#64748B] font-semibold text-xs ml-2">{STEP_LABELS[o.step] || o.step}</span></p>
                <p className="text-xs text-[#64748B] mt-1">{o.email} · {o.phone}{o.city ? ` · ${o.city}` : ""}{o.cf ? ` · CF ${o.cf}` : ""}</p>
                {(o.registries?.length > 0 || o.topic) && <p className="text-[11px] text-[#94A3B8] mt-1">{[o.topic, ...(o.registries || [])].filter(Boolean).join(" · ")}</p>}
              </div>
              <span className={`text-[10px] font-bold uppercase tracking-wider border rounded-full px-3 py-1.5 ${STATUS_COLORS[o.status] || ""}`}>
                {STATUS_LABELS[o.status] || o.status}
              </span>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {["in_lavorazione", "visure_pronte", "attesa_banca", "cancellata", "non_cancellata"].map((s) => (
                <button key={s} data-testid={`admin-status-${s}-${o.id.slice(0, 8)}`} onClick={() => setStatus(o.id, s)}
                  className="text-[11px] font-semibold border border-[#E2E8F0] bg-white rounded-full px-3 py-1.5 text-[#1A1A1A] hover:border-[#00AEEF]/60 transition-colors duration-200">
                  → {STATUS_LABELS[s]}
                </button>
              ))}
              {o.status === "cancellata" && (
                <span className="inline-flex items-center gap-1.5 flex-wrap">
                  <select data-testid={`admin-saldo-pkg-${o.id.slice(0, 8)}`} value={saldoPkg[o.id] || "cancellazione_999"}
                    onChange={(e) => setSaldoPkg((p) => ({ ...p, [o.id]: e.target.value }))}
                    className="text-[11px] font-semibold border border-[#E2E8F0] rounded-full px-2 py-1.5 text-[#1A1A1A] bg-white">
                    {Object.entries(PKG_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                  <button data-testid={`admin-saldo-${o.id.slice(0, 8)}`} onClick={() => saldoLink(o.id)}
                    className="text-[11px] font-semibold border border-[#00AEEF]/50 text-[#0077A3] bg-[#E6F6FD] rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 hover:bg-[#00AEEF]/20 transition-colors duration-200">
                    <Link2 size={11} /> Genera link saldo
                  </button>
                  <button data-testid={`admin-sblocco-${o.id.slice(0, 8)}`} onClick={() => inviaSblocco(o.id)}
                    className="text-[11px] font-semibold border border-[#003087]/40 text-[#003087] bg-white rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 hover:bg-[#E6F6FD] transition-colors duration-200">
                    <Mail size={11} /> Invia link «paga e sblocca»
                  </button>
                  <label data-testid={`admin-visura-upload-${o.id.slice(0, 8)}`}
                    className="text-[11px] font-semibold border border-[#E2E8F0] text-[#64748B] bg-white rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 cursor-pointer hover:border-[#00AEEF]/60 transition-colors duration-200">
                    <FileText size={11} /> {o.visura_pulita ? "Visura caricata ✓ — sostituisci" : "Carica visura pulita (PDF)"}
                    <input type="file" accept="application/pdf" className="hidden" onChange={(e) => uploadVisura(o.id, e.target.files?.[0])} />
                  </label>
                </span>
              )}
              {o.sblocco_url && (
                <button onClick={() => { navigator.clipboard?.writeText(o.sblocco_url); toast.success("Link sblocco copiato"); }}
                  className="text-[11px] font-semibold border border-[#003087]/30 text-[#003087] rounded-full px-3 py-1.5 inline-flex items-center gap-1.5">
                  <Copy size={11} /> Copia link sblocco
                </button>
              )}
              <button data-testid={`admin-expand-${o.id.slice(0, 8)}`} onClick={() => { const nx = expanded === o.id ? null : o.id; setExpanded(nx); if (nx) loadLetters(o.id); }}
                className="text-[11px] font-semibold border border-[#E2E8F0] bg-white rounded-full px-3 py-1.5 text-[#1A1A1A] inline-flex items-center gap-1.5 hover:border-[#00AEEF]/60 transition-colors duration-200">
                <FileText size={11} /> Lettere PEC
              </button>
              {o.saldo_url && (
                <button onClick={() => { navigator.clipboard?.writeText(o.saldo_url); toast.success("Link copiato"); }}
                  className="text-[11px] font-semibold border border-[#00AEEF]/40 text-[#00AEEF] rounded-full px-3 py-1.5 inline-flex items-center gap-1.5">
                  <Copy size={11} /> Copia link saldo
                </button>
              )}
            </div>

            {expanded === o.id && (
              <div className="mt-5 border-t border-[#E2E8F0] pt-5">
                <div className="mb-6 bg-[#F6FAFD] border border-[#E2E8F0] rounded-2xl p-4" data-testid={`admin-esito-${o.id.slice(0, 8)}`}>
                  {o.documenti_fase1 && (
                    <p className="text-[11px] font-bold text-[#0077A3] mb-3" data-testid={`admin-fase1-${o.id.slice(0, 8)}`}>
                      ✓ Delega + Privacy firmate il {new Date(o.documenti_fase1.at).toLocaleDateString("it-IT")} — CF {o.documenti_fase1.dati?.cf}
                      {o.documenti_fase1.firma_path && (
                        <button onClick={() => apriFirma(o.id, "delega_privacy")} data-testid={`admin-firma1-${o.id.slice(0, 8)}`}
                          className="ml-2 underline font-semibold text-[#003087]">vedi firma</button>
                      )}
                      {o.allegati?.carta_identita && (
                        <button onClick={() => apriAllegato(o.id, "carta_identita")} data-testid={`admin-doc-ci-${o.id.slice(0, 8)}`}
                          className="ml-2 underline font-semibold text-[#003087]">carta identità</button>
                      )}
                      {o.allegati?.tessera_sanitaria && (
                        <button onClick={() => apriAllegato(o.id, "tessera_sanitaria")} data-testid={`admin-doc-ts-${o.id.slice(0, 8)}`}
                          className="ml-2 underline font-semibold text-[#003087]">tessera sanitaria</button>
                      )}
                    </p>
                  )}
                  <p className="text-xs font-bold text-[#003087] mb-3">Esito visure → pacchetto proposto in automatico:</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <input type="number" min="0" max="50" placeholder="N° segnalazioni" data-testid={`admin-esito-count-${o.id.slice(0, 8)}`}
                      value={esito[o.id]?.count ?? o.esito_visure?.segnalazioni ?? ""}
                      onChange={(e) => setEsito((p) => ({ ...p, [o.id]: { ...p[o.id], count: e.target.value } }))}
                      className="input-field !w-40 text-sm" />
                    <input type="text" placeholder="Note (opzionale)"
                      value={esito[o.id]?.note ?? o.esito_visure?.note ?? ""}
                      onChange={(e) => setEsito((p) => ({ ...p, [o.id]: { ...p[o.id], note: e.target.value } }))}
                      className="input-field flex-1 min-w-40 text-sm" />
                    <button data-testid={`admin-esito-save-${o.id.slice(0, 8)}`} onClick={() => saveEsito(o.id)}
                      className="btn-green rounded-full px-4 py-2 text-xs font-bold">Salva esito</button>
                  </div>
                  {o.pacchetto_proposto && (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span className="text-[11px] font-bold text-[#0077A3] bg-[#E6F6FD] border border-[#00AEEF]/40 rounded-full px-3 py-1.5" data-testid={`admin-pkg-${o.id.slice(0, 8)}`}>
                        Pacchetto: {PKG_LABELS[o.pacchetto_proposto]}
                      </span>
                      <button data-testid={`admin-offerta-${o.id.slice(0, 8)}`} onClick={() => inviaOfferta(o.id)}
                        className="text-[11px] font-semibold border border-[#00AEEF]/50 text-[#0077A3] bg-white rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 hover:bg-[#E6F6FD] transition-colors duration-200">
                        <Mail size={11} /> Invia offerta via email
                      </button>
                      {o.offerta_inviata_at && !o.contratto && (
                        <span className="text-[10px] font-semibold text-[#94A3B8]">offerta inviata · in attesa di firma</span>
                      )}
                      {o.contratto && (
                        <span className="text-[11px] font-bold text-white bg-[#00AEEF] rounded-full px-3 py-1.5" data-testid={`admin-firmato-${o.id.slice(0, 8)}`}>
                          Contratto firmato da {o.contratto.nome_firma}
                        </span>
                      )}
                      {o.contratto?.firma_path && (
                        <button onClick={() => apriFirma(o.id, "contratto")} data-testid={`admin-firma2-${o.id.slice(0, 8)}`}
                          className="text-[11px] font-semibold underline text-[#003087]">vedi firma</button>
                      )}
                    </div>
                  )}
                </div>

                <p className="text-xs font-bold text-[#003087] mb-3">1 · Seleziona gli istituti segnalanti e genera le lettere:</p>
                <div className="flex flex-wrap gap-2">
                  {pec.map((p) => {
                    const pid = p.id;
                    const on = (selected[o.id] || []).includes(pid);
                    return (
                      <button key={pid} onClick={() =>
                        setSelected((prev) => ({ ...prev, [o.id]: on ? (prev[o.id] || []).filter((x) => x !== pid) : [...(prev[o.id] || []), pid] }))}
                        className={`text-[11px] font-medium rounded-full px-3 py-1.5 border transition-colors duration-200 ${on ? "border-[#00AEEF] bg-[#E6F6FD] text-[#0077A3]" : "border-[#E2E8F0] bg-white text-[#64748B]"}`}>
                        {p.name}
                      </button>
                    );
                  })}
                </div>
                <button data-testid={`admin-genletters-${o.id.slice(0, 8)}`} onClick={() => genLetters(o.id)}
                  className="btn-green mt-4 rounded-full px-5 py-2.5 text-xs font-bold">
                  Genera lettere di cancellazione
                </button>

                <div className="mt-6 space-y-3">
                  {(letters[o.id] || []).map((l) => (
                    <div key={l.id} className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-xl p-4">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-xs text-[#003087] font-bold">{l.istituto} <span className="text-[#94A3B8] font-medium">→ {l.pec}</span></p>
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-bold uppercase tracking-wider ${l.status === "pronta" ? "text-[#94A3B8]" : "text-[#00AEEF]"}`}>
                            {l.status === "pronta" ? "Pronta" : l.status === "inviata_simulata" ? "Invio simulato" : "Inviata"}
                          </span>
                          {l.status === "pronta" && (
                            <button data-testid={`admin-send-${l.id.slice(0, 8)}`} onClick={() => sendLetter(o.id, l.id)}
                              className="text-[11px] font-semibold border border-[#00AEEF]/50 text-[#0077A3] rounded-full px-3 py-1 inline-flex items-center gap-1 hover:bg-[#E6F6FD] transition-colors duration-200">
                              <Send size={10} /> Invia PEC
                            </button>
                          )}
                        </div>
                      </div>
                      <details className="mt-2">
                        <summary className="text-[11px] text-[#94A3B8] cursor-pointer">Vedi testo lettera</summary>
                        <pre className="mt-2 text-[11px] text-[#1A1A1A] whitespace-pre-wrap max-h-56 overflow-y-auto">{l.body}</pre>
                      </details>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
        {orders.length === 0 && <p className="text-[#94A3B8] text-sm">Nessuna richiesta ancora.</p>}
      </div>
    </div>
  );
}
